import { inject, injectable, interfaces } from 'inversify';
import { TRAINING_VARIANT } from '../../../../modules/jobs/jobs.model';
import { MarkingToolType } from '../../../../modules/projects/details/stats/annotationTools/models/AnnotationToolsStats';
import { ACDLError } from '../../../models/error.model';
import { ProjectRole } from '../../../models/userRole.model';
import { IMainApiService } from '../../../services/api.service';
import { IApiService } from '../../../services/api.service.base';
import { IApiServiceTraining } from '../../../services/api.service.training';
import { QuestionType } from '../../annotation/question.model';
import {
  ICanPublishProjectResponse,
  IDatasetsTabDto,
  IMembers,
  IProjectDetails,
  IUpdateDatasetsOrderRequest,
  IUpdateImageAssignmentPolicyRequest,
  IUpdateProjectDetailsPayload,
} from '../projectDetails.models';

export interface IProjectDetailsApiService {
  assignDatasetAsync(projectId: string, datasetId: string): Promise<void | ACDLError>;
  unlinkDatasetAsync(projectId: string, datasetId: string): Promise<void | ACDLError>;
  getProjectDetailsAsync(projectId: string): Promise<IProjectDetails | ACDLError>;
  getProjectDetailsMembersAsync(projectId: string): Promise<IMembers | ACDLError>;
  getProjectDetailsDatasetsAsync(projectId: string, workspaceId: string): Promise<IDatasetsTabDto | ACDLError>;
  updateProjectDetailsAsync(detailsPayload: IUpdateProjectDetailsPayload): Promise<void | ACDLError>;
  addUserToProjectAsync(projectId: string, email: string, role: ProjectRole): Promise<void | ACDLError>;
  removeUserFromProjectAsync(userId: string, projectId: string): Promise<void | ACDLError>;
  canPublishAsync(projectId: string): Promise<ICanPublishProjectResponse | ACDLError>;
  publishAsync(projectId: string, discardDrafts: boolean): Promise<void | ACDLError>;
  checkProjectNameUniquenessAsync(projectId: string, workspaceId: string, name: string): Promise<boolean | ACDLError>;
  createProjectAsync(projectId: string, projectName: string, trainingVariant: TRAINING_VARIANT | null, workspaceId: string): Promise<void | ACDLError>;
  updateProjectDatasetsOrderAsync(request: IUpdateDatasetsOrderRequest): Promise<void | ACDLError>;
  updateProjectImageAssignmentPolicy(request: IUpdateImageAssignmentPolicyRequest): Promise<void | ACDLError>;
  getProjectAnnotationTemplateAsync(
    workspaceId: string,
    trainingVariant: TRAINING_VARIANT
  ): Promise<{ question: null | QuestionType; marking_tool: null | MarkingToolType } | ACDLError>;
}

export namespace IProjectDetailsApiService {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsApiService> = Symbol('PROJECT_DETAILS_API_SERVICE');
}

@injectable()
export class ProjectDetailsApiService implements IProjectDetailsApiService {
  constructor(
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IApiServiceTraining.$) private readonly apiTrainingService: IApiService
  ) {}

  async createProjectAsync(projectId: string, projectName: string, trainingVariant: TRAINING_VARIANT | null, workspaceId: string): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/ProjectDraft/Create', { projectId, projectName, workspaceId, modelVariant: trainingVariant });
  }

  async publishAsync(projectId: string, discardDrafts: boolean): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/projectdraft/publish', { discardDrafts, projectDraftId: projectId });
  }

  async checkProjectNameUniquenessAsync(projectId: string, workspaceId: string, name: string): Promise<boolean | ACDLError> {
    return await this.apiService.getAsync<boolean>('/Projects/CheckProjectNameUniqueness', { params: { projectId, workspaceId, name } });
  }

  async canPublishAsync(projectId: string): Promise<ICanPublishProjectResponse | ACDLError> {
    return await this.apiService.getAsync<ICanPublishProjectResponse>('/ProjectDraft/CanPublish', { params: { projectId } });
  }

  async assignDatasetAsync(projectId: string, datasetId: string): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/AssignDataset', { projectId, datasetId });
  }

  async unlinkDatasetAsync(projectId: string, datasetId: string): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/UnlinkDataset', { projectId, datasetId });
  }

  async getProjectDetailsAsync(projectId: string): Promise<ACDLError | IProjectDetails> {
    return await this.apiService.getAsync<IProjectDetails>('/Projects/Details', { params: { projectId } });
  }

  async getProjectDetailsMembersAsync(projectId: string): Promise<ACDLError | IMembers> {
    return await this.apiService.getAsync<IMembers>('/Projects/Members', { params: { projectId } });
  }

  async getProjectDetailsDatasetsAsync(projectId: string, workspaceId: string): Promise<IDatasetsTabDto | ACDLError> {
    const url = '/Projects/Datasets';
    return await this.apiService.getAsync<IDatasetsTabDto>(url, { params: { projectId, workspaceId } });
  }

  async updateProjectDetailsAsync(detailsPayload: IUpdateProjectDetailsPayload): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/UpdateProjectDetails', detailsPayload);
  }

  async addUserToProjectAsync(projectId: string, email: string, role: ProjectRole): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/AddUser', { projectId, role, userEmail: email });
  }

  async removeUserFromProjectAsync(userId: string, projectId: string): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/RemoveUser', { userId, projectId });
  }

  async updateProjectDatasetsOrderAsync(request: IUpdateDatasetsOrderRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/UpdateDatasetsOrder', request);
  }

  async updateProjectImageAssignmentPolicy(request: IUpdateImageAssignmentPolicyRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/UpdateImageAssignmentPolicy', request);
  }

  async getProjectAnnotationTemplateAsync(
    workspaceId: string,
    trainingVariant: TRAINING_VARIANT
  ): Promise<{ question: null | QuestionType; marking_tool: null | MarkingToolType } | ACDLError> {
    return await this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/model-variants/${trainingVariant}/annotation-tool`);
  }
}
